import React from 'react';

import { ControlProps, GroupBase, components } from 'react-select';

import styles from './FilterControl.module.scss';

export interface FilterControlConfig {
    /**
     * Icon to display at the start of the control
     */
    icon?: React.ReactNode;
    /**
     * Filter name label to display before the value (e.g., "Projects")
     */
    filterName?: string;
}

/**
 * Factory function that creates a custom Control component with icon and filterName prefix.
 * Use this when you want to add an icon and/or filter label to a Select component.
 *
 * @example Outside component:
 *
 * ```tsx
 * const FilterControl = createFilterControl({
 *     icon: <MuiIcon icon="folder" type={'outlined'} color={'inherit'} />,
 *     filterName: 'Projects',
 * });
 * ```
 *
 * @example Inside component:
 * IMPORTANT: Always memoize the result to prevent focus issues with react-select.
 *
 * ```tsx
 * import { useMemo } from 'react';
 * import { createFilterControl } from 'components/DesignSystem/Select/components/FilterControl';
 * import { FolderIcon } from 'your-icon-library';
 *
 * const MyComponent = () => {
 *     // Memoize to keep stable reference across renders
 *     const FilterControl = useMemo(
 *         () => createFilterControl({
 *             icon: <FolderIcon size={16} />,
 *             filterName: "Projects"
 *         }),
 *         []
 *     );
 *
 *     return (
 *         <Select
 *             components={{
 *                 Control: FilterControl,
 *                 Option: InputOption,
 *                 ValueContainer: ValueContainer,
 *             }}
 *             // ... other props
 *         />
 *     );
 * };
 * ```
 */
export const createFilterControl = (config: FilterControlConfig) => {
    const { icon, filterName } = config;

    const FilterControl = <
        Option = unknown,
        IsMulti extends boolean = boolean,
        Group extends GroupBase<Option> = GroupBase<Option>,
    >(
        props: ControlProps<Option, IsMulti, Group>,
    ) => {
        const { children } = props;
        const hasPrefix = icon || filterName;

        // Override control styles of Select component.
        const customInnerProps = {
            ...props.innerProps,
            style: {
                ...props.innerProps?.style,
                flexWrap: 'nowrap' as const,
            },
        };

        return (
            <components.Control {...props} innerProps={customInnerProps}>
                {hasPrefix && (
                    <div className={styles.prefixContainer}>
                        {icon && <span className={styles.iconWrapper}>{icon}</span>}
                        {filterName && <span className={styles.filterName}>{filterName}:</span>}
                    </div>
                )}
                {children}
            </components.Control>
        );
    };

    FilterControl.displayName = 'FilterControl';

    return FilterControl;
};
